
import { fuelCardRepository } from './repositories/fuelCardRepository';
import { stockLocationRepository } from './repositories/stockLocationRepository';
import { stockLedgerService } from './stockLedgerService';
import { FuelCard, StockLocation } from '../types';
import { v4 as uuidv4 } from 'uuid';

export const fuelCardService = {
    async list(organizationId: string): Promise<(FuelCard & { balance: number })[]> {
        const cards = await fuelCardRepository.list(organizationId);

        // Enrich with balance
        // In a real app we might dataload this.
        // For now, serial.
        const result: (FuelCard & { balance: number })[] = [];

        for (const card of cards) {
            // Find balance for this location
            // What is the stockItemId for "Money on Card"?
            // Usually Fuel Cards track Liters (if limits) or Money?
            // In this system, Fuel Cards track FUEL volume (Liters).
            // But which fuel? 
            // Multi-user usually treats Fuel Cards as Generic "Fuel" or specific items.
            // Let's assume we sum ALL fuel items for total volume? 
            // Or we need a virtual 'FUEL_CARD_BALANCE' item.
            // Given the requirement: "Перестать считать баланс полем".
            // Let's assume the Ledger tracks specific Fuel Type (AI-92) or just "Volume".
            // Simpler: Just get balance of ALL items and sum them? 
            // Or picking a specific stockItem?
            // Let's return balance of a "VIRTUAL_FUEL" item or sum of all.
            // For MVP: Let's assume Balance is tracked in a generic unit (Liters) if we don't distinguish types rigidly on the card.
            // OR, we just fetch balance for *active* StockItems.
            // Let's return 0 for now and implement `getBalance(cardId, fuelType)` later?
            // UI expects a single number "Balance".
            // Let's use a constant 'FUEL_CARD_BALANCE_ITEM' for now.

            const balance = await stockLedgerService.getBalanceAt(card.stockLocationId, 'FUEL_GENERIC');
            result.push({ ...card, balance });
        }

        return result;
    },

    async create(data: { cardNumber: string; driverId?: string; organizationId?: string; initialBalance?: number }): Promise<FuelCard> {
        // 1. Create Stock Location
        const orgId = data.organizationId || 'current';

        // Check duplication
        const list = await fuelCardRepository.list(orgId);
        if (list.some(c => c.cardNumber === data.cardNumber)) {
            throw new Error(`Карта с номером ${data.cardNumber} уже существует`);
        }

        const location = await stockLocationRepository.create({
            organizationId: orgId,
            type: 'FUEL_CARD',
            name: `Fuel Card ${data.cardNumber}`,
            fuelCardId: 'pending'
        });

        // 2. Create Card
        const card = await fuelCardRepository.create({
            cardNumber: data.cardNumber,
            driverId: data.driverId,
            organizationId: orgId,
            stockLocationId: location.id,
            isActive: true
        });

        // 3. Link back
        location.fuelCardId = card.id;
        await stockLocationRepository.save(location);

        // 4. Initial Balance (Adjustment)
        if (data.initialBalance && data.initialBalance !== 0) {
            await stockLedgerService.createIncome({ // Using Income or Adjustment? Income is simpler for "From nowhere"
                stockLocationId: location.id,
                stockItemId: 'FUEL_GENERIC', // Using standard item
                quantity: data.initialBalance,
                documentType: 'INITIAL_IMPORT',
                documentId: card.id,
                externalRef: `INIT:${card.cardNumber}`
            });
        }

        return card;
    },

    async update(id: string, data: Partial<FuelCard>): Promise<void> {
        const card = await fuelCardRepository.get(id);
        if (!card) throw new Error('Card not found');

        const updated = { ...card, ...data };
        await fuelCardRepository.save(updated);
    },

    async assignToDriver(cardId: string, driverId: string): Promise<void> {
        const card = await fuelCardRepository.get(cardId);
        if (card) {
            card.driverId = driverId;
            // Clean up old assignments? 
            // One driver -> One card? Or Many?
            // If 1:1, we should unassign other cards from this driver.
            // Let's assume simple assignment for now.
            await fuelCardRepository.save(card);
        }
    },

    async findActiveByDriver(driverId: string): Promise<FuelCard | undefined> {
        const cards = await fuelCardRepository.findByDriverId(driverId);
        return cards.find(c => c.isActive);
    }
};
